/**
 * Field mapping for SHOPLINE and Shopify CSV formats
 * Supports 35 common fields covering 95% of conversion scenarios
 */

// Field mappings for each platform (35 common fields)
const FIELD_MAPPINGS = {
  shopify: {
    // Core fields (10)
    id: 'Handle',
    handle: 'Handle',
    title: 'Title',
    subtitle: null, // Shopify doesn't have a direct subtitle field
    description: 'Body (HTML)',
    price: 'Variant Price',
    compareAtPrice: 'Variant Compare At Price',
    stock: 'Variant Inventory Qty',
    brand: 'Vendor',
    spu: null, // No direct SPU equivalent in Shopify

    // Variant fields (8)
    sku: 'Variant SKU',
    option1Name: 'Option1 Name',
    option1Value: 'Option1 Value',
    option2Name: 'Option2 Name',
    option2Value: 'Option2 Value',
    option3Name: 'Option3 Name',
    option3Value: 'Option3 Value',
    variantImage: 'Variant Image',

    // SEO fields (4)
    seoTitle: 'SEO Title',
    seoDescription: 'SEO Description',
    path: null, // Shopify uses handle-based URLs
    seoKeywords: null, // Shopify deprecated keywords meta

    // Image fields (3)
    images: 'Image Src',
    imageAlt: 'Image Alt Text',
    imagePosition: 'Image Position',

    // Inventory & Logistics fields (6)
    weight: 'Variant Weight',
    weightUnit: 'Variant Weight Unit',
    barcode: 'Variant Barcode',
    hsCode: 'Variant HS Code',
    shippingOrigin: null, // Uses location settings
    fulfillmentService: 'Variant Fulfillment Service',
    inventoryTracker: 'Variant Inventory Tracker',
    inventoryPolicy: 'Variant Inventory Policy',

    // Other fields (4)
    tags: 'Tags',
    collections: 'Type', // Shopify uses 'Type' for product type/category
    status: 'Status',
    published: 'Published',
    taxCode: 'Variant Tax Code',
    chargeTax: 'Variant Taxable',
    productCategory: 'Product Category',
    customProductType: 'Type',
    costPerItem: 'Cost per item',
    requiresShipping: 'Variant Requires Shipping',
    giftCard: 'Gift Card',
  },

  shopline: {
    // Core fields (10)
    id: 'spuid',
    handle: 'Handle',
    title: 'Title*',
    subtitle: 'Subtitle',
    description: 'Product description html',
    price: 'SKU price',
    compareAtPrice: 'SKU compare at price',
    stock: 'SKU Inventory Quantity',
    brand: 'Vendor',
    spu: 'SPU',

    // Variant fields (8)
    sku: 'SKU',
    option1Name: 'Option1 name',
    option1Value: 'Option1 value',
    option2Name: 'Option2 name',
    option2Value: 'Option2 value',
    option3Name: 'Option3 name',
    option3Value: 'Option3 value',
    variantImage: 'Image',

    // SEO fields (4)
    seoTitle: 'SEO title',
    seoDescription: 'SEO description',
    path: 'Path',
    seoKeywords: 'SEO keywords',

    // Image fields (3)
    images: 'Master image',
    imageAlt: 'Image alt text',
    imagePosition: 'Image position',

    // Inventory & Logistics fields (6)
    weight: 'SKU weight',
    weightUnit: 'SKU weight unit',
    barcode: 'Barcode (ISBN, UPC, GTIN, etc.)',
    hsCode: 'HS code',
    shippingOrigin: 'Shipping origin',
    fulfillmentService: 'Fulfillment service',
    inventoryTracker: 'SKU Inventory Tracker',
    inventoryPolicy: 'SKU Inventory Policy',

    // Other fields (4)
    tags: 'Tags',
    collections: 'Collections',
    status: 'Status',
    published: 'Published on online store',
    taxCode: 'Tax code',
    chargeTax: 'Charge tax',
    productCategory: 'Standardized Product Type',
    customProductType: 'Custom Product Type',
    costPerItem: 'Cost per item',
    requiresShipping: 'Requires shipping',
    giftCard: 'Gift card',
  },

  'shopline-v1': {
    // Core fields (10)
    id: 'Product ID (DO NOT EDIT)',
    handle: null, // SHOPLINE 1.0 doesn't have handle, generated from title
    title: 'Product Name (English)',
    subtitle: null,
    description: 'Product Summary (Traditional Chinese)',
    price: 'Regular Price',
    compareAtPrice: 'Sale Price',
    stock: 'Quantity (DO NOT EDIT)',
    brand: 'Brand',
    spu: null,

    // Variant fields (8)
    sku: 'SKU',
    option1Name: null, // SHOPLINE 1.0 variants are in same row
    option1Value: 'Variant (English) (DO NOT EDIT)',
    option2Name: null,
    option2Value: null,
    option3Name: null,
    option3Value: null,
    variantImage: 'Image',

    // SEO fields (4)
    seoTitle: 'SEO Title (English)',
    seoDescription: 'SEO Description (English)',
    path: null, // Generated from handle
    seoKeywords: 'SEO Keywords',

    // Image fields (3)
    images: 'Image',
    imageAlt: null, // SHOPLINE 1.0 doesn't have alt text
    imagePosition: null,

    // Inventory & Logistics fields (6)
    weight: 'Weight(KG)',
    weightUnit: null, // Always KG in SHOPLINE 1.0
    barcode: 'Barcode',
    hsCode: null,
    shippingOrigin: null,
    fulfillmentService: null,
    inventoryTracker: null,
    inventoryPolicy: null,

    // Other fields (4)
    tags: 'Product Tag',
    collections: 'Online Store Categories',
    status: 'Hidden Product', // Y/N for hidden
    published: 'Online Store Status', // Y/N for published
    taxCode: null,
    chargeTax: null,
    productCategory: null,
    customProductType: null,
    costPerItem: 'Product Cost',
    requiresShipping: null,
    giftCard: null,
  }
};

/**
 * Get field name for current platform
 * @param {'shopline' | 'shopify' | 'shopline-v1'} platform - Platform type
 * @param {string} fieldName - Unified field name
 * @returns {string|null} - Platform-specific field name
 */
export function getFieldName(platform, fieldName) {
  const mapping = FIELD_MAPPINGS[platform];
  if (!mapping) {return null;}
  return mapping[fieldName] || null;
}

/**
 * Get field value from row data using platform-specific mapping
 * @param {Object} row - CSV row data
 * @param {'shopline' | 'shopify' | 'shopline-v1'} platform - Platform type
 * @param {string} fieldName - Unified field name
 * @returns {string} - Field value or empty string
 */
export function getFieldValue(row, platform, fieldName) {
  const columnName = getFieldName(platform, fieldName);
  if (!columnName) {return '';}
  return row[columnName] || '';
}

/**
 * Parse tags/collections string into array
 * @param {string} tagsString - Comma-separated tags
 * @returns {string[]} - Array of tags
 */
function parseTags(tagsString) {
  if (!tagsString) {return [];}
  return tagsString
    .split(',')
    .map(tag => tag.trim())
    .filter(tag => tag.length > 0);
}

/**
 * Parse numeric value safely
 * @param {*} value - Value to parse
 * @param {number} defaultValue - Default value if parsing fails
 * @returns {number}
 */
function parseNumeric(value, defaultValue = 0) {
  if (value === null || value === undefined || value === '') {return defaultValue;}
  const parsed = parseFloat(value);
  return isNaN(parsed) ? defaultValue : parsed;
}

/**
 * Parse boolean value
 * @param {*} value - Value to parse
 * @returns {boolean|null}
 */
function parseBoolean(value) {
  if (value === null || value === undefined || value === '') {return null;}
  const str = String(value).toLowerCase().trim();
  if (['true', 't', 'yes', 'y', '1', 'active', 'published', 'continue'].includes(str)) {return true;}
  if (['false', 'f', 'no', 'n', '0', 'inactive', 'unpublished', 'draft', 'deny'].includes(str)) {return false;}
  return null;
}

/**
 * Generate handle from product title (for shopline-v1)
 * @param {string} title - Product title
 * @param {number} index - Row index for uniqueness
 * @returns {string} - Generated handle
 */
function generateHandle(title, index) {
  if (!title) return `product-${index}`;

  return title
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '') // Remove special characters
    .trim()
    .replace(/\s+/g, '-') // Replace spaces with hyphens
    .replace(/-+/g, '-') // Replace multiple hyphens with single
    .substring(0, 50); // Limit length
}

/**
 * Build unified product entity from platform-specific row data
 * @param {Object} row - CSV row data
 * @param {'shopline' | 'shopify' | 'shopline-v1'} platform - Platform type
 * @param {number} index - Row index
 * @returns {Object} - Unified product entity with 35 fields
 */
export function buildUnifiedProduct(row, platform, index) {
  const getValue = (field) => getFieldValue(row, platform, field);
  const getNumeric = (field) => parseNumeric(getValue(field), null);
  const getInt = (field) => {
    const val = parseInt(getValue(field));
    return isNaN(val) ? 0 : val;
  };
  const getBool = (field) => parseBoolean(getValue(field));

  let handle = getValue('handle');

  // For shopline-v1, generate handle from title if not present
  if (platform === 'shopline-v1' && !handle) {
    const title = getValue('title') || row['Product Name (Traditional Chinese)'] || 'Untitled Product';
    handle = generateHandle(title, index);
  }

  // Build path based on platform
  const path = (platform === 'shopify' || platform === 'shopline-v1') && handle
    ? `/products/${handle}`
    : getValue('path');

  // Build images array
  const images = [];
  const mainImage = getValue('images');
  if (mainImage) {images.push(mainImage);}
  const variantImage = getValue('variantImage');
  if (variantImage && variantImage !== mainImage) {images.push(variantImage);}

  // Build options array for variants
  const options = [];
  for (let i = 1; i <= 3; i++) {
    const name = getValue(`option${i}Name`);
    const value = getValue(`option${i}Value`);
    if (name && value) {
      options.push({ name, position: i, value });
    }
  }

  // For shopline-v1, handle variant field specially
  if (platform === 'shopline-v1') {
    const variantValue = row['Variant (English) (DO NOT EDIT)'] || row['Variant (Traditional Chinese) (DO NOT EDIT)'];
    if (variantValue) {
      options.push({ name: 'Variant', position: 1, value: variantValue });
    }
  }

  // Parse tags and collections
  const tags = parseTags(getValue('tags'));
  const collections = parseTags(getValue('collections'));

  // Handle special status for shopline-v1 (Y/N)
  let status = getValue('status') || 'active';
  let published = getBool('published');

  if (platform === 'shopline-v1') {
    // In shopline-v1, Hidden Product = Y means status is hidden
    status = row['Hidden Product'] === 'Y' ? 'hidden' : 'active';
    // Online Store Status = Y means published
    published = row['Online Store Status'] === 'Y';
  }

  return {
    // ========== Core Fields (10) ==========
    id: getValue('id') || `${platform}-product-${index}`,
    semantic_id: handle || `product-${index}`,
    handle: handle,
    title: platform === 'shopline-v1'
      ? (getValue('title') || row['Product Name (Traditional Chinese)'] || 'Untitled Product')
      : (getValue('title') || 'Untitled Product'),
    subtitle: getValue('subtitle') || null,
    description: getValue('description') || null,
    price: getNumeric('price'),
    compare_at_price: getNumeric('compareAtPrice'),
    stock: getInt('stock'),
    brand: getValue('brand') || null,
    spu: getValue('spu') || null,

    // ========== Variant Fields (8) ==========
    sku: getValue('sku') || null,
    options: options,
    option1_name: options[0]?.name || null,
    option1_value: options[0]?.value || null,
    option2_name: options[1]?.name || null,
    option2_value: options[1]?.value || null,
    option3_name: options[2]?.name || null,
    option3_value: options[2]?.value || null,

    // ========== SEO Fields (4) ==========
    seo_title: getValue('seoTitle') || null,
    seo_description: getValue('seoDescription') || null,
    path: path,
    seo_keywords: getValue('seoKeywords') || null,

    // ========== Image Fields (3) ==========
    images: images,
    image_alt: getValue('imageAlt') || null,
    image_position: getInt('imagePosition') || 1,
    variant_image: getValue('variantImage') || null,

    // ========== Inventory & Logistics (6) ==========
    weight: getNumeric('weight'),
    weight_unit: getValue('weightUnit') || (platform === 'shopline-v1' ? 'kg' : 'g'),
    barcode: getValue('barcode') || null,
    hs_code: getValue('hsCode') || null,
    shipping_origin: getValue('shippingOrigin') || null,
    fulfillment_service: getValue('fulfillmentService') || null,
    inventory_tracker: getValue('inventoryTracker') || null,
    inventory_policy: getValue('inventoryPolicy') || null,

    // ========== Other Fields (4) ==========
    tags: tags,
    collections: collections,
    status: status,
    published: published,
    tax_code: getValue('taxCode') || null,
    charge_tax: getBool('chargeTax'),
    product_category: getValue('productCategory') || null,
    custom_product_type: getValue('customProductType') || null,
    cost_per_item: getNumeric('costPerItem'),
    requires_shipping: getBool('requiresShipping'),
    gift_card: getBool('giftCard'),

    // ========== Metadata ==========
    platform: platform,
    updated_at: new Date().toISOString(),
    raw: row
  };
}

/**
 * Get all available field names for a platform
 * @param {'shopline' | 'shopify' | 'shopline-v1'} platform - Platform type
 * @returns {string[]} - Array of unified field names
 */
export function getAvailableFields(platform) {
  const mapping = FIELD_MAPPINGS[platform];
  if (!mapping) {return [];}
  return Object.keys(mapping).filter(key => mapping[key] !== null);
}

/**
 * Check if a field is supported for a platform
 * @param {'shopline' | 'shopify' | 'shopline-v1'} platform - Platform type
 * @param {string} fieldName - Unified field name
 * @returns {boolean}
 */
export function isFieldSupported(platform, fieldName) {
  const mapping = FIELD_MAPPINGS[platform];
  if (!mapping) {return false;}
  return mapping[fieldName] !== undefined && mapping[fieldName] !== null;
}
